<template>
  <v-navigation-drawer :value="value" fixed right temporary width="620" class="order-drawer" @input="$emit('input', $event)">
    <div v-if="value && item" class="order-drawer__layout">
      <!-- La file d'où l'on vient, et où l'on en est dedans -->
      <div class="order-drawer__head">
        <v-icon small class="mr-2">{{ icon }}</v-icon>
        <span class="text-caption text--secondary">{{ $t(`onboarding.queue.step.${queue}`) }}<template v-if="position"> · {{ position }}</template></span>
        <v-spacer />
        <v-btn icon small :title="$t('close')" @click="$emit('input', false)"><v-icon small>mdi-close</v-icon></v-btn>
      </div>

      <div class="order-drawer__body">
        <registration-approval-panel v-if="queue === 'registrations'" :registration-id="item.id" @changed="$emit('changed')" />

        <consent-decision-panel v-else-if="queue === 'consents'" :row="item" @decided="$emit('changed', { left: true })" />

        <template v-else>
          <v-skeleton-loader v-if="loading && !order" type="list-item-three-line, card" />
          <v-alert v-else-if="error" type="error" dense outlined>{{ error }}</v-alert>

          <template v-else-if="order">
            <!-- Qui, quoi, où elle en est -->
            <div class="d-flex align-center flex-wrap mb-2" style="gap: 8px">
              <v-icon>{{ kindIcon(order.kind) }}</v-icon>
              <span class="text-h6 font-weight-regular">{{ beneficiaryOf(order) }}</span>
              <v-chip small :color="statusColor(order.status)" dark>{{ statusLabel(order.status) }}</v-chip>
              <v-chip v-if="order.step && order.step !== queue && !['done', 'closed'].includes(order.step)" small outlined color="primary">
                {{ $t('onboarding.queue.drawer.now-in', { queue: $t(`onboarding.queue.step.${order.step}`) }) }}
              </v-chip>
            </div>

            <v-simple-table dense class="order-drawer__facts mb-3">
              <tbody>
                <tr><th>{{ $t('onboarding.commissioning.kind-label') }}</th><td>{{ kindOf(order) }}</td></tr>
                <tr>
                  <th>{{ $t('organization.organization') }}</th>
                  <td><router-link target="_blank" :to="{ name: 'organizations.detail', params: { id: order.organizationId } }">{{ order.organizationName || '—' }}</router-link></td>
                </tr>
                <tr v-if="order.holder === 'member'">
                  <th>{{ $t('onboarding.commissioning.person') }}</th>
                  <td>
                    <router-link v-if="order.memberId" target="_blank" :to="{ name: 'member', params: { memberId: order.memberId } }">{{ personName(order) }}</router-link>
                    <span v-else>{{ personName(order) }}</span>
                    <span v-if="order.person && order.person.email" class="text-caption"> · {{ order.person.email }}</span>
                    <span v-if="order.person && order.person.phone" class="text-caption"> · {{ order.person.phone }}</span>
                  </td>
                </tr>
                <tr v-if="isInstallable(order)"><th>{{ $t('onboarding.queue.column.place') }}</th><td>{{ addressOf(order) }}</td></tr>
                <tr v-if="order.desiredDate"><th>{{ $t('onboarding.commissioning.desired-date') }}</th><td>{{ dayOf(order.desiredDate) }}</td></tr>
                <tr>
                  <th>{{ $t('onboarding.queue.column.requestedBy') }}</th>
                  <td>{{ requesterOf(order) }} · {{ dayOf(order.createdAt) }}</td>
                </tr>
                <tr v-if="order.stepSince && !['done', 'closed'].includes(order.step)">
                  <th>{{ $t('onboarding.queue.column.since') }}</th><td>{{ order.stepSince | formatDate }}</td>
                </tr>
              </tbody>
            </v-simple-table>

            <order-field-issue :order="order" />
            <order-plan-failure :order="order" :resumable="isInstallable(order) && canActivate" />

            <!-- Ce qu'on attend, et de qui -->
            <v-alert v-if="expected" color="amber lighten-5" dense class="black--text text-body-2">
              <b>{{ $t('onboarding.commissioning.expected-action') }} :</b> {{ expected }}
            </v-alert>
            <v-alert v-else-if="['cancelled', 'rejected'].includes(order.status)" type="warning" dense text class="text-body-2">
              {{ $t(`onboarding.commissioning.closed-because.${order.status}`, { reason: order.closingReason || '—' }) }}
            </v-alert>

            <!-- Le panneau de l'étape : de quoi continuer sans quitter la file -->
            <div v-if="order.step !== 'closed'" class="order-drawer__panel">
              <template v-if="order.step === 'approval'">
                <div v-if="mayManageOnboarding" class="d-flex align-center">
                  <span class="text-body-2">{{ $t('onboarding.queue.drawer.approval') }}</span>
                  <v-spacer />
                  <order-approval-actions :order="order" @updated="onUpdated" />
                </div>
                <p v-else class="text-caption text--secondary mb-0">{{ $t('onboarding.queue.drawer.approval-by-manager') }}</p>
              </template>

              <template v-else-if="order.step === 'person'">
                <order-requirements-list :order="order" :only="personRequirements" />
                <div v-if="mayFollowOrders" class="d-flex align-center">
                  <span class="text-caption text--secondary">{{ $t('onboarding.queue.drawer.person') }}</span>
                  <v-spacer />
                  <order-reminder-action :order="order" @updated="onUpdated" />
                </div>
              </template>

              <order-badge-panel v-else-if="['badge-validation', 'shipment', 'handover', 'activation'].includes(order.step)" :order="order" @updated="onUpdated" @changed="reload(true)" />

              <order-survey-panel v-else-if="order.step === 'survey'" :order="order" @updated="onUpdated" />

              <template v-else-if="['scheduling', 'installation'].includes(order.step)">
                <order-scheduling-panel :order="order" :technicians="technicians" @updated="onUpdated" />
                <!-- Poser depuis le back-office : rare (le technicien pose depuis le terrain), replié -->
                <v-expansion-panels v-if="canActivate" flat class="mt-4">
                  <v-expansion-panel>
                    <v-expansion-panel-header class="px-0 text-body-2">{{ $t('onboarding.queue.drawer.activate-here') }}</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <commissioning-activation :key="order.id + order.status" :order="order" @updated="reload(true)" />
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </template>

              <template v-else-if="order.step === 'commissioning'">
                <commissioning-activation v-if="isInstallable(order) && canActivate" :key="order.id + order.status" :order="order" @updated="reload(true)" />
                <template v-else>
                  <plan-steps v-if="order.plan" :steps="order.plan.steps" />
                  <order-commissioning-retry v-if="canRetry" class="mt-3" :order="order" @updated="reload(true)" />
                </template>
              </template>

              <template v-else-if="order.step === 'done'">
                <div class="text-body-2">{{ $t('onboarding.queue.drawer.done', { equipment: order.equipmentLabel || order.equipmentId || '—', date: dayOf(order.closedAt) }) }}</div>
              </template>
            </div>
          </template>
        </template>
      </div>

      <!-- Sortir vers la fiche (nouvel onglet : la file reste), ou passer à la suivante -->
      <div class="order-drawer__foot">
        <v-btn small text color="primary" target="_blank" :to="fullPage">
          <v-icon left small>mdi-open-in-new</v-icon>{{ $t(`onboarding.queue.drawer.open-full.${kindOfQueue}`) }}
        </v-btn>
        <v-spacer />
        <v-btn small color="primary" :disabled="!hasNext" @click="$emit('next')">
          {{ $t('onboarding.queue.drawer.next') }}<v-icon right small>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import CommissioningRepository from '@repository/CommissioningRepository'
import CommissioningMixin, { PERSON_REQUIREMENTS } from '../CommissioningMixin'
import CommissioningActivation from '../CommissioningActivation.vue'
import PlanSteps from '../PlanSteps.vue'
import OrderApprovalActions from '../Order/OrderApprovalActions.vue'
import OrderBadgePanel from '../Order/OrderBadgePanel.vue'
import OrderFieldIssue from '../Order/OrderFieldIssue.vue'
import OrderPlanFailure from '../Order/OrderPlanFailure.vue'
import OrderReminderAction from '../Order/OrderReminderAction.vue'
import OrderCommissioningRetry from '../Order/OrderCommissioningRetry.vue'
import OrderRequirementsList from '../Order/OrderRequirementsList.vue'
import OrderSchedulingPanel from '../Order/OrderSchedulingPanel.vue'
import OrderSurveyPanel from '../Order/OrderSurveyPanel.vue'
import RegistrationApprovalPanel from '../Registration/RegistrationApprovalPanel.vue'
import ConsentDecisionPanel from '../Consent/ConsentDecisionPanel.vue'

/**
 * Le tiroir de la file : un clic sur une ligne l'ouvre sur ce qu'il faut savoir (qui, quoi, où, depuis quand, ce qu'on
 * attend) et sur le panneau de l'étape — valider, relancer, préparer ou remettre des badges, rendre la visite, planifier,
 * reprendre une mise en service —, sans quitter la file. Une commande qui change d'étape reste ouverte sur sa nouvelle
 * étape ; « Suivante » enchaîne. Une inscription à valider et une demande de retrait de consentement s'y traitent aussi.
 */
export default {
  name: 'OrderDrawer',

  components: {
    CommissioningActivation, PlanSteps, OrderApprovalActions, OrderCommissioningRetry, OrderBadgePanel, OrderFieldIssue, OrderPlanFailure, OrderReminderAction,
    OrderRequirementsList, OrderSchedulingPanel, OrderSurveyPanel, RegistrationApprovalPanel, ConsentDecisionPanel,
  },

  mixins: [CommissioningMixin],

  props: {
    value: { type: Boolean, default: false },
    // La file d'où l'on vient : une étape de commande, `registrations` ou `consents`.
    queue: { type: String, required: true },
    // La ligne choisie (une commande, une inscription ou une demande de retrait).
    item: { type: Object, default: null },
    icon: { type: String, default: 'mdi-inbox-full-outline' },
    position: { type: String, default: null },
    hasNext: { type: Boolean, default: false },
    technicians: { type: Array, default: null },
    // Dans l'onglet d'une organisation : la fiche complète s'ouvre sous elle, pas en pleine page.
    organizationId: { type: String, default: null },
  },

  data() {
    return { order: null, loading: false, error: null, personRequirements: [...PERSON_REQUIREMENTS, 'activation'] }
  },

  computed: {
    kindOfQueue() {
      return ['registrations', 'consents'].includes(this.queue) ? this.queue : 'orders'
    },

    /** Relancer une mise en service sans pose : ZEborne (le module), une commande dont plus rien ne se pose. */
    canRetry() {
      return this.mayManageOnboarding && !!this.order && !this.isInstallable(this.order)
    },

    canActivate() {
      return this.mayManageOnboarding && (this.currentUser?.permissions || []).includes('STATION_ADD')
    },

    expected() {
      return this.order ? this.expectedActionOf(this.order) : ''
    },

    fullPage() {
      if (!this.item) return {}
      if (this.queue === 'registrations') {
        // Rouverte depuis l'adresse, la ligne n'a que son identifiant : l'ancienne adresse du dossier y renvoie.
        return this.item.organizationId
          ? { name: 'organizations.members.registration', params: { id: this.item.organizationId, registrationId: this.item.id } }
          : { name: 'onboarding.registrations.detail', params: { id: this.item.id } }
      }
      if (this.queue === 'consents') return { name: 'customer.detail', params: { id: this.item.customer.id } }
      if (this.organizationId) return { name: 'organizations.equipment-order', params: { id: this.organizationId, orderId: this.item.id } }
      return { name: 'onboarding.commissionings.detail', params: { id: this.item.id } }
    },
  },

  watch: {
    // Une autre ligne (ou la même rouverte) : la commande se relit en entier, plan compris.
    item: {
      immediate: true,
      handler(item, previous) {
        if (!item || this.kindOfQueue !== 'orders') return
        if (previous && previous.id === item.id && this.order) return
        this.order = null
        this.reload(false)
      },
    },
  },

  methods: {
    /** Relire la commande ; après un geste (`changed`), la file se recompte derrière. */
    async reload(changed) {
      if (!this.item) return
      const id = this.item.id
      this.loading = true
      this.error = null
      try {
        const order = await CommissioningRepository.get(id)
        if (this.item && this.item.id === id) this.order = order
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.loading = false
      }
      if (changed) this.$emit('changed')
    },

    /** Un geste a rendu la commande à jour : le tiroir la montre (plan gardé), la file se recompte derrière. */
    onUpdated(order) {
      this.order = { ...this.order, ...order }
      this.$emit('changed')
    },

    requesterOf(order) {
      const r = order.requestedBy || {}
      return r.name || this.$t(`onboarding.queue.requester.${r.type}`)
    },
  },
}
</script>

<style scoped>
.order-drawer__layout {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.order-drawer__head {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.order-drawer__body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 16px;
}
.order-drawer__foot {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.order-drawer__facts th {
  width: 160px;
  color: #616161;
  font-weight: 400;
  text-align: left;
}
.order-drawer__panel {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 12px;
}
</style>
